import { getProperErrorMessage } from 'helpers/errors';

export const INSUFFICIENT_SOCIAL_MEDIA_PERMISSIONS_ERROR =
  'InsufficientSocialMediaPermissions';

export const SOCIAL_MEDIA_CONNECTION_EXPIRED_ERROR =
  'SocialMediaConnectionExpiredException';

export const SOCIAL_MEDIA_CONNECTION_NOT_FOUND_ERROR =
  'SocialMediaConnectionNotFound';

export const SOCIAL_MEDIA_CONNECTION_NOT_FOUND_MESSAGE =
  'No social media connection found. Please connect your account.';

export const INSUFFICIENT_SOCIAL_MEDIA_PERMISSIONS_MESSAGE =
  'The permissions you chose are insufficient to complete this connection. Please reconnect and grant all required permissions.';

export type SocialIntegrationErrorDialogData = {
  title: string;
  subTitle?: string;
  message: string;
};

const DEFAULT_INSUFFICIENT_PERMISSIONS_DIALOG: SocialIntegrationErrorDialogData = {
  title: 'Insufficient permissions',
  subTitle: 'We could not complete this connection',
  message: INSUFFICIENT_SOCIAL_MEDIA_PERMISSIONS_MESSAGE,
};

const getGraphQLErrorPayload = (
  error: unknown
): {
  message?: string;
  extensions?: {
    code?: string;
    response?: { error?: string; message?: string | string[] };
    originalError?: { error?: string };
    exception?: { response?: { error?: string } };
  };
} | null => {
  const apolloError = error as {
    message?: string;
    graphQLErrors?: Array<{
      message?: string;
      extensions?: {
        code?: string;
        response?: { error?: string; message?: string | string[] };
        originalError?: { error?: string };
        exception?: { response?: { error?: string } };
      };
    }>;
  };

  return apolloError?.graphQLErrors?.[0] ?? null;
};

const isInsufficientSocialMediaPermissionsError = (error: unknown): boolean => {
  const graphQLError = getGraphQLErrorPayload(error);
  const errorName =
    graphQLError?.extensions?.response?.error ||
    graphQLError?.extensions?.originalError?.error ||
    graphQLError?.extensions?.exception?.response?.error;
  const message =
    graphQLError?.message || (error as { message?: string })?.message;

  return (
    errorName === INSUFFICIENT_SOCIAL_MEDIA_PERMISSIONS_ERROR ||
    message === INSUFFICIENT_SOCIAL_MEDIA_PERMISSIONS_MESSAGE ||
    Boolean(message?.includes('permissions you chose are insufficient'))
  );
};

export const isSocialMediaConnectionExpiredError = (
  error: unknown
): boolean => {
  const graphQLError = getGraphQLErrorPayload(error);
  const responseMessage = graphQLError?.extensions?.response?.message;
  const messages = [
    graphQLError?.message,
    ...(Array.isArray(responseMessage) ? responseMessage : [responseMessage]),
    (error as { message?: string })?.message,
  ];

  return messages.includes(SOCIAL_MEDIA_CONNECTION_EXPIRED_ERROR);
};

export const isSocialMediaConnectionNotFoundError = (
  error: unknown
): boolean => {
  const graphQLError = getGraphQLErrorPayload(error);
  const responseMessage = graphQLError?.extensions?.response?.message;
  const messages = [
    graphQLError?.message,
    graphQLError?.extensions?.response?.error,
    ...(Array.isArray(responseMessage) ? responseMessage : [responseMessage]),
    (error as { message?: string })?.message,
  ];

  return messages.some(
    (message) =>
      message === SOCIAL_MEDIA_CONNECTION_NOT_FOUND_ERROR ||
      message === SOCIAL_MEDIA_CONNECTION_NOT_FOUND_MESSAGE
  );
};

export const getSocialIntegrationErrorDialog = (
  error: unknown
): SocialIntegrationErrorDialogData | null => {
  if (!isInsufficientSocialMediaPermissionsError(error)) {
    return null;
  }

  const graphQLError = getGraphQLErrorPayload(error);
  const responseMessage = graphQLError?.extensions?.response?.message;
  const resolvedResponseMessage = Array.isArray(responseMessage)
    ? responseMessage[0]
    : responseMessage;

  return {
    ...DEFAULT_INSUFFICIENT_PERMISSIONS_DIALOG,
    message:
      resolvedResponseMessage ||
      graphQLError?.message ||
      getProperErrorMessage(
        error,
        DEFAULT_INSUFFICIENT_PERMISSIONS_DIALOG.message
      ),
  };
};
